import { CSSProperties } from 'react'
import cn from 'classnames'
import styles from './MentionAvatar.module.scss'
import {
  getMentionAvatarComponent,
  getSalesforceCloudUrl,
  isSalesforceAvatar,
} from './mentionHelpers'

interface IProps {
  avatar: string
  name: string
  className?: string
  // When provided, sets the avatar's width/height; otherwise falls back to DEFAULT_AVATAR_PX.
  size?: number
}

// Bitmap avatars (Salesforce cloud PNG, legacy URL icons) have no intrinsic
// sizing, so without this they render at full natural resolution. SVG icons
// carry their own small size, so this only governs the <img> fallbacks.
const DEFAULT_AVATAR_PX = 14

// Recovers the icon key from a legacy persisted asset URL like
// `…/note-mention-client-underwriting-icon.<hash>.svg`. Non-greedy so multi-part
// keys (e.g. client-underwriting) stop at the first `-icon`.
const NOTE_MENTION_KEY_RE = /note-mention-(.+?)-icon/

/**
 * Renders a mention's avatar. Salesforce entities use the full-color cloud PNG;
 * all other mention icons are inline SVGs whose `currentColor` inherits the
 * surrounding text color so they stay visible in both light and dark mode.
 */
const MentionAvatar = ({ avatar, name, className, size }: IProps) => {
  const avatarSize = size ?? DEFAULT_AVATAR_PX
  const imgClassName = cn(styles.avatarImage, className)

  const salesforceUrl = isSalesforceAvatar(avatar) ? getSalesforceCloudUrl() : null
  if (salesforceUrl) {
    return (
      <img
        className={imgClassName}
        src={salesforceUrl}
        alt={name}
        style={{ '--mention-avatar-size': avatarSize } as CSSProperties}
      />
    )
  }

  // Avatars are normally icon keys ('client', 'entity', …) resolved to inline
  // SVGs. Legacy content persisted the resolved asset URL instead
  // (…/note-mention-<key>-icon.<hash>.svg), whose hash changes every build — so
  // rendering the stored URL 404s after a deploy. Recover the key from the URL
  // and resolve the current build's SVG.
  const key = avatar.includes('/') ? NOTE_MENTION_KEY_RE.exec(avatar)?.[1] : avatar
  const Icon = key ? getMentionAvatarComponent(key) : null
  if (Icon) {
    return <Icon className={className} {...(size ? { width: size, height: size } : {})} />
  }

  // A genuine non-icon image URL (or an unrecognized key) — render as-is.
  if (avatar.includes('/') || avatar.endsWith('.svg')) {
    return (
      <img
        className={imgClassName}
        src={avatar}
        alt={name}
        style={{ '--mention-avatar-size': avatarSize } as CSSProperties}
      />
    )
  }

  return null
}

export default MentionAvatar
